import { useState } from 'react'
import {
  CUSTOMER_INFO_ITEM_LABEL,
  CUSTOMER_INFO_ITEMS,
  DEFAULT_SAMPLE_OFFER_TYPE,
  productCategoryLabel,
  SAMPLE_COST_BEARER_LABEL,
  SAMPLE_COST_BEARERS,
  SAMPLE_CURRENCIES,
  SAMPLE_FULFILMENT_LABEL,
  SAMPLE_OFFER_STATUSES,
  SAMPLE_OFFER_TYPE_LABEL,
  SAMPLE_OFFER_TYPES,
  SAMPLE_ORDER_STATUS_LABEL,
  SAMPLE_PORTAL_COPY,
  SAMPLE_WEEKDAY_LABEL,
  sampleOfferChargesPrice,
  sampleOfferIsFree,
  type FactorySampleOfferInput,
  type SampleFreeCost,
  type FactorySampleOrderView,
  type Language,
  type SampleOfferView,
  type SampleProgressInput,
} from '@repo/shared'
import { saveBlob } from '../samples-api'

/**
 * 샘플 제안 편집과 주문 카드 (AI 중계 샘플). 공장 포털(4개 언어)과 관리자 [샘플 거래](ko)가 같은 구성 요소를 쓴다.
 * 공장 포털이 Clerk 만 있는 별도 화면이라 kumo 대신 일반 HTML 요소로 그린다 (pages/factory.tsx 와 같은 방식)
 */

const field = 'flex flex-col gap-1 text-sm'
const input = 'rounded border border-gray-300 px-2 py-1'
const button =
  'rounded bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50'

/** 새 제안은 유료 시제품으로 시작한다 — 무료 방식은 기본값이 아니다 (대표 결정 2차 B) */
const EMPTY: FactorySampleOfferInput = {
  offerType: DEFAULT_SAMPLE_OFFER_TYPE,
  status: 'draft',
  terms: {
    currency: 'KRW',
    price: null,
    leadDays: 7,
    creditedToOrder: false,
    requiredInfo: [],
    tastingRegions: [],
    tastingWeekdays: [],
    consultingMinutes: null,
    deliverables: '',
    note: '',
    freeCost: null,
  },
}

const EMPTY_FREE_COST: SampleFreeCost = {
  bearer: 'factory',
  budgetAmount: null,
  budgetQuantity: null,
  note: '',
}

const toggle = <T,>(list: readonly T[], value: T) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

/** 제안 하나 편집. 유형에 맞지 않는 칸은 숨기고, 저장할 때 비운다 (서버가 유형·조건 짝을 다시 검사한다) */
export function SampleOfferForm({
  lang,
  offer,
  disabled,
  onSave,
}: {
  lang: Language
  offer: SampleOfferView | null
  disabled: boolean
  onSave: (input: FactorySampleOfferInput) => void
}) {
  const t = SAMPLE_PORTAL_COPY[lang]
  const [draft, setDraft] = useState<FactorySampleOfferInput>(
    offer
      ? {
          offerType: offer.offerType,
          status: offer.status,
          terms: offer.terms,
        }
      : EMPTY,
  )
  const terms = draft.terms
  const setTerms = (patch: Partial<typeof terms>) =>
    setDraft({ ...draft, terms: { ...terms, ...patch } })
  const priced = sampleOfferChargesPrice(draft.offerType)
  const tasting = draft.offerType === 'free_tasting'
  const consulting = draft.offerType === 'paid_consulting'
  const free = sampleOfferIsFree(draft.offerType)
  const freeCost = terms.freeCost ?? EMPTY_FREE_COST
  const setFreeCost = (patch: Partial<SampleFreeCost>) =>
    setTerms({ freeCost: { ...freeCost, ...patch } })
  const submit = () =>
    onSave({
      ...draft,
      terms: {
        ...terms,
        price: priced ? terms.price : null,
        tastingRegions: tasting ? terms.tastingRegions : [],
        tastingWeekdays: tasting ? terms.tastingWeekdays : [],
        consultingMinutes: consulting ? (terms.consultingMinutes ?? 60) : null,
        freeCost: free ? freeCost : null,
      },
    })
  return (
    <form
      className="grid gap-3 rounded border border-gray-200 p-4 md:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <label className={field}>
        {t.type}
        <select
          className={input}
          value={draft.offerType}
          onChange={(e) =>
            setDraft({
              ...draft,
              offerType: e.target.value as FactorySampleOfferInput['offerType'],
            })
          }
        >
          {SAMPLE_OFFER_TYPES.map((type) => (
            <option key={type} value={type}>
              {SAMPLE_OFFER_TYPE_LABEL[lang][type]}
            </option>
          ))}
        </select>
      </label>
      <label className={field}>
        {t.status}
        <select
          className={input}
          value={draft.status}
          onChange={(e) =>
            setDraft({ ...draft, status: e.target.value as FactorySampleOfferInput['status'] })
          }
        >
          {SAMPLE_OFFER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t.offerStatus[s]}
            </option>
          ))}
        </select>
      </label>
      {/* 공장 이름·넓은 지역·가능 분류는 공장 목록(factories)에서 읽는다 — 여기서 고치지 않는다 */}
      {offer && (
        <p className="text-sm text-gray-600 md:col-span-2">
          {t.factoryName}: {offer.factoryName} · {t.anonRegion}: {offer.anonRegion} · {t.categories}
          : {offer.categoryIds.map((id) => productCategoryLabel(id, lang)).join(', ')}
        </p>
      )}
      <label className={field}>
        {t.currency}
        <select
          className={input}
          value={terms.currency}
          onChange={(e) =>
            setTerms({ currency: e.target.value as FactorySampleOfferInput['terms']['currency'] })
          }
        >
          {SAMPLE_CURRENCIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </label>
      {priced ? (
        <label className={field}>
          {t.price}
          <input
            className={input}
            type="number"
            min={1}
            required
            value={terms.price ?? ''}
            onChange={(e) => setTerms({ price: e.target.value ? Number(e.target.value) : null })}
          />
        </label>
      ) : (
        <p className="self-end text-xs text-gray-500">{t.priceHint}</p>
      )}
      <label className={field}>
        {t.leadDays}
        <input
          className={input}
          type="number"
          min={1}
          max={120}
          required
          value={terms.leadDays}
          onChange={(e) => setTerms({ leadDays: Number(e.target.value) })}
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={terms.creditedToOrder}
          onChange={(e) => setTerms({ creditedToOrder: e.target.checked })}
        />
        {t.credited}
      </label>
      <fieldset className="md:col-span-2">
        <legend className="text-sm">{t.requiredInfo}</legend>
        <div className="flex flex-wrap gap-2">
          {CUSTOMER_INFO_ITEMS.map((item) => (
            <label key={item} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={terms.requiredInfo.includes(item)}
                onChange={() => setTerms({ requiredInfo: toggle(terms.requiredInfo, item) })}
              />
              {CUSTOMER_INFO_ITEM_LABEL[lang][item]}
            </label>
          ))}
        </div>
      </fieldset>
      {tasting ? (
        <>
          <label className={field}>
            {t.tastingRegions}
            <input
              className={input}
              value={terms.tastingRegions.join(', ')}
              onChange={(e) =>
                setTerms({
                  tastingRegions: e.target.value
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>
          <fieldset>
            <legend className="text-sm">{t.tastingWeekdays}</legend>
            <div className="flex flex-wrap gap-2">
              {SAMPLE_WEEKDAY_LABEL[lang].map((label, day) => (
                <label key={label} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={terms.tastingWeekdays.includes(day)}
                    onChange={() =>
                      setTerms({ tastingWeekdays: toggle(terms.tastingWeekdays, day) })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
        </>
      ) : null}
      {consulting ? (
        <label className={field}>
          {t.consultingMinutes}
          <input
            className={input}
            type="number"
            min={10}
            max={6000}
            value={terms.consultingMinutes ?? 60}
            onChange={(e) => setTerms({ consultingMinutes: Number(e.target.value) })}
          />
        </label>
      ) : null}
      {free ? (
        <fieldset className="grid gap-2 rounded border border-amber-300 p-3 md:col-span-2 md:grid-cols-3">
          <legend className="text-sm font-semibold">{t.freeCostTitle}</legend>
          <p className="text-xs text-gray-600 md:col-span-3">{t.freeCostHint}</p>
          <label className={field}>
            {t.costBearer}
            <select
              className={input}
              value={freeCost.bearer}
              onChange={(e) => setFreeCost({ bearer: e.target.value as SampleFreeCost['bearer'] })}
            >
              {SAMPLE_COST_BEARERS.map((b) => (
                <option key={b} value={b}>
                  {SAMPLE_COST_BEARER_LABEL[lang][b]}
                </option>
              ))}
            </select>
          </label>
          <label className={field}>
            {t.budgetAmount} ({terms.currency})
            <input
              className={input}
              type="number"
              min={1}
              value={freeCost.budgetAmount ?? ''}
              onChange={(e) =>
                setFreeCost({ budgetAmount: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>
          <label className={field}>
            {t.budgetQuantity}
            <input
              className={input}
              type="number"
              min={1}
              step={1}
              value={freeCost.budgetQuantity ?? ''}
              onChange={(e) =>
                setFreeCost({ budgetQuantity: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>
          <label className={`${field} md:col-span-3`}>
            {t.freeCostNote}
            <input
              className={input}
              maxLength={300}
              value={freeCost.note}
              onChange={(e) => setFreeCost({ note: e.target.value })}
            />
          </label>
        </fieldset>
      ) : null}
      <p className="text-xs text-gray-500 md:col-span-2">{t.publicTextHint}</p>
      <label className={`${field} md:col-span-2`}>
        {t.deliverables}
        <input
          className={input}
          maxLength={500}
          value={terms.deliverables}
          onChange={(e) => setTerms({ deliverables: e.target.value })}
        />
      </label>
      <label className={`${field} md:col-span-2`}>
        {t.note}
        <textarea
          className={input}
          maxLength={1000}
          value={terms.note}
          onChange={(e) => setTerms({ note: e.target.value })}
        />
      </label>
      <div className="md:col-span-2">
        <button type="submit" className={button} disabled={disabled}>
          {t.save}
        </button>
      </div>
    </form>
  )
}

/**
 * 주문 하나 — 공장이 보는 그대로. 결제 전에는 '고객 #번호'와 요청만, 결제 뒤에는 배합·공정·원가 사양서와 동의한 정보.
 * 할 일은 버튼 하나: 내려받기(CSV·JSON) · 발송·일정 기록
 */
export function SampleOrderCard({
  lang,
  order,
  disabled,
  onProgress,
  onCsv,
}: {
  lang: Language
  order: FactorySampleOrderView
  disabled: boolean
  onProgress: (input: SampleProgressInput) => void
  /** 없으면 CSV 버튼을 숨긴다 (관리자 화면 — CSV 는 공장 포털 경로가 만든다) */
  onCsv?: () => void
}) {
  const t = SAMPLE_PORTAL_COPY[lang]
  const [note, setNote] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const needsSchedule = order.offerType === 'free_tasting' || order.offerType === 'paid_consulting'
  const spec = order.spec
  const canProgress =
    order.paymentStatus === 'paid' &&
    (order.fulfilmentStatus === 'spec_sent' || order.fulfilmentStatus === 'in_progress')
  const downloadJson = () =>
    spec &&
    saveBlob(
      new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' }),
      `recipecat-spec-${order.customerRef}.json`,
    )
  return (
    <article className="space-y-3 rounded border border-gray-200 p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">
          {t.customer(order.customerRef)} · {SAMPLE_OFFER_TYPE_LABEL[lang][order.offerType]}
        </h3>
        <span className="text-sm">{SAMPLE_ORDER_STATUS_LABEL[lang][order.status]}</span>
      </header>
      {order.brief.request ? (
        <p className="text-sm">
          <strong>{t.brief}</strong> {order.brief.request}
        </p>
      ) : null}
      {order.dueAt ? (
        <p className="text-sm">
          {t.due}: {new Date(order.dueAt).toLocaleDateString(lang)}
        </p>
      ) : null}
      {order.fulfilment ? (
        <p className="text-sm">
          {SAMPLE_FULFILMENT_LABEL[lang][order.fulfilment.kind]} · {order.fulfilment.note}
        </p>
      ) : null}
      {!spec ? (
        <p className="text-sm text-gray-500">{t.specLocked}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {onCsv ? (
              <button type="button" className={button} onClick={onCsv}>
                {t.downloadCsv}
              </button>
            ) : null}
            <button type="button" className={button} onClick={downloadJson}>
              {t.downloadJson}
            </button>
          </div>
          <p className="text-sm font-semibold">
            {spec.product.name} · {spec.product.subtype} · {spec.product.format} ·{' '}
            {spec.product.netWeight.amount}
            {spec.product.netWeight.unit}
          </p>
          <h4 className="text-sm font-semibold">{t.formulation}</h4>
          <table className="w-full text-left text-sm">
            <tbody>
              {spec.formulation.map((i) => (
                <tr key={i.name} className="border-t border-gray-100">
                  <td>{i.name}</td>
                  <td>
                    {i.qty}
                    {i.unit}
                  </td>
                  <td>{i.ratioPct === null ? '' : `${i.ratioPct}%`}</td>
                  <td className="text-gray-500">{i.fn}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4 className="text-sm font-semibold">{t.process}</h4>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {spec.process.map((s) => (
              <li key={s.no}>
                <strong>{s.title}</strong> — {s.temp} · {s.time} · {s.equipment}. {s.desc}
              </li>
            ))}
          </ol>
          <p className="text-sm">
            {t.cost}: {spec.costAssumptions.currency} {spec.costAssumptions.totalPerUnit}
          </p>
        </>
      )}
      {order.disclosed.length ? (
        <div className="text-sm">
          <h4 className="font-semibold">{t.disclosed}</h4>
          {order.disclosed.map((d) => (
            <p key={d.item}>
              {CUSTOMER_INFO_ITEM_LABEL[lang][d.item]}: {d.value}
            </p>
          ))}
        </div>
      ) : null}
      {canProgress ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            onProgress({
              note,
              scheduledAt:
                needsSchedule && scheduledAt ? new Date(scheduledAt).toISOString() : null,
            })
          }}
        >
          <label className={field}>
            {t.progressNote}
            <input
              className={input}
              required
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          {needsSchedule ? (
            <label className={field}>
              {t.scheduledAt}
              <input
                className={input}
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </label>
          ) : null}
          <button type="submit" className={button} disabled={disabled}>
            {t.recordProgress}
          </button>
        </form>
      ) : null}
    </article>
  )
}
